import * as React from 'react';
import Plot from 'react-plotly.js';
import { useTheme } from '@mui/material/styles';
import {
    Stack,
    TextField,
    MenuItem,
    CircularProgress,
    Typography,
    Grid,
    Paper,
    Switch
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const categories = {
    Category10: ['#1f77b4','#d62728','#9467bd','#fb9a99','#8c564b','#e377c2','#7f7f7f','#bcbd22','#17becf','#7fc97f','#ff7f00','#cab2d6','#6a3d9a','#b15928','#fdc086','#386cb0',
                    '#f0027f','#666666','#a6cee3','#33a02c','#fbb4ae','#b3cde3','#ccebc5','#1a85ff','#d41159','#00bfc4','#a6761d','#39b600','#ff6db6','#b2182b','#2166ac','#9970ab',
                    '#e08214','#6a51a3','#f4a582', '#80cdc1','#e41a1c','#377eb8','#4daf4a','#984ea3','#ffff33','#a65628','#f781bf','#66c2a5','#fc8d62','#a6d854','#ffd92f','#1b9e77',
                      '#ff1493', '#00ced1', '#8b0000', '#228b22', '#ff4500', '#2e8b57', '#4682b4', '#da70d6', '#ffb6c1', '#20b2aa', '#ffa500', '#9acd32', '#4169e1', '#ff6347',
                      '#6b8e23', '#40e0d0', '#c71585', '#00fa9a', '#cd5c5c', '#483d8b'  ], //66 colors
    Tableau28: [ '#ff1493', '#00ced1', '#8b0000', '#228b22', '#ff6347', '#40e0d0', '#ff8c00', '#556b2f','#da70d6', '#4682b4', '#cd5c5c', '#6b8e23', '#ffa07a', '#20b2aa', '#f0e68c',
                '#9932cc', '#7fff00', '#ff4500', '#5f9ea0', '#bc8f8f', '#7b68ee', '#8fbc8f','#ffd700', '#6a5acd', '#2f4f4f', '#afeeee', '#cd853f','#8b4513', '#d2691e', '#6495ed', '#ffb6c1', '#708090', '#ffa500', '#66cdaa', '#993366', '#4169e1', '#db7093',
                '#008080', '#a6cee3', '#1f78b4', '#b2df8a','#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#cab2d6', '#6a3d9a', '#b15928', '#1b9e77', '#d95f02', '#7570b3','#e7298a', '#66a61e', '#e6ab02', '#a6761d',
                '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f', '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab'], //66 colors
    Pastel25: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4', '#fed9a6', '#ffffcc', '#e5d8bd', '#fddaec','#b3e2cd', '#fdcdac', '#cbd5e8',
            '#f4cae4', '#e6f5c9', '#fff2ae', '#f1e2cc','#d5e8d4', '#f8cecc',  '#dae8fc',  '#fff2cc',  '#e1d5e7','#fbe5d6',  '#e2efda','#fde9d9',  '#e6e0ec',  '#f2dcdb'],
};

const LogDataPlot = ({tableId, category, reloadEvent, auth, onLogout}) => {
    // console.log("Table ID:", tableId);
    const [xData, setXData] = React.useState([]);
    const [multiSeries, setMultiSeries] = React.useState([]);
    const [colorScheme, setColorScheme] = React.useState('Category10');
    const [showSpikes, setShowSpikes] = React.useState(false); // NEW
    const [hoverMode, setHoverMode] = React.useState('x'); // NEW
       const [loading, setLoading] = React.useState(false);
    const theme = useTheme();

    // Fetch data when tableId changes and on reloadEvent
    // Reset loading state and set a timeout to show loader if fetch takes too long
    React.useEffect(() => {
        let isMounted = true;
        setLoading(false); // Always reset loading at the start
        let timer = setTimeout(() => { if (isMounted) setLoading(true); }, 500); // Show loader if fetch takes >500ms
        async function fetchData() {
            if (!tableId) return;
            const response = await fetch(`${API_BASE_URL}/rawdata/table-data?tableName=${category}_${tableId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${auth.token}`
                }
            });
            const data = await response.json();
             if (isMounted) {
            setXData(data["Tid"] || []);
            setMultiSeries(data["data_set"] || []);
            clearTimeout(timer); // Cancel loader if data arrives before 500ms
            setLoading(false);   // Hide loader
        }
        }
        fetchData();

         return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [tableId, reloadEvent, auth?.token]); // Added reloadEvent to dependencies

    // Prepare Plotly traces
    const plotData = multiSeries.map((s, i) => ({
        x: xData,
        y: s.data,
        type: 'scatter',
        mode: 'lines',
        name: s.label,
        line: {
            color: categories[colorScheme][i % categories[colorScheme].length],
            width: 2,
        },
    }));

    const layout = {
        title: '',
        xaxis: {
            title: { text: 'Tid', font: { color: theme.palette.text.primary, size: 14 } },
            tickfont: { color: theme.palette.text.primary },
            gridcolor: theme.palette.divider,
            showspikes: showSpikes,
            spikemode: 'across',
            spikesnap: 'cursor',
            spikethickness: 2,
            spikecolor: 'auto',
        },
        yaxis: {
            title: { text: 'Value', font: { color: theme.palette.text.primary, size: 14 } },
            tickfont: { color: theme.palette.text.primary },
            gridcolor: theme.palette.divider,
            showspikes: showSpikes,
            spikemode: 'across',
            spikesnap: 'cursor',
            spikethickness: 2,
            spikecolor: 'auto',
        },
        legend: {
            orientation: 'v',
            x: 1.05,
            y: 1,
            font: { color: theme.palette.text.primary },
        },
        plot_bgcolor: theme.palette.background.paper,
        paper_bgcolor: theme.palette.background.paper,
        autosize: true,
        height: 700,
        minwidth: '100%',
        hovermode: hoverMode,
    };

    return (

        <Grid container spacing={4} >
        <Paper
          sx={{
            mb: 4,
            width: "100%",
            overflowX: "auto", // enables horizontal scroll on small screens
            p: 2,
          }}
          elevation={15}>
        <Grid size={{ xs: 12 }} >
            <div style={{ position: "relative"}}>
                 <Plot
                            data={plotData}
                            layout={layout}
                            useResizeHandler={true}
                            style={{ width: "100%", height: "100%" }}
                            config={{ responsive: true }}
                        />

                        {loading && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    background: "rgba(255,255,255,0.7)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    zIndex: 10
                                }}
                            >
                                <CircularProgress />
                            </div>
                        )}
            </div>

                    {/* Toggle Spike Lines Button */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                    <Typography>Show Spike Lines</Typography>
                    <Switch
                        checked={showSpikes}
                        onChange={() => setShowSpikes((v) => !v)}
                        color="primary"
                    />
                    </Stack>
                      {/* Hover Mode Selector */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 2 }}>
                        <Typography>Hover Mode</Typography>
                        <TextField
                            select
                            value={hoverMode}
                            onChange={e => setHoverMode(e.target.value)}
                            size="small"
                            sx={{ width: 120 }}
                        >
                            <MenuItem value="x">Compare Data (Compare on X)</MenuItem>
                            <MenuItem value="closest">Show Closest Data (Single Point)</MenuItem>
                        </TextField>
                    </Stack>
                {/* ...color palette picker... */}
                <TextField
                    select
                    value={colorScheme}
                    onChange={(e) => setColorScheme(e.target.value)}
                >
                    {Object.entries(categories).map(([name, colors]) => (
                        <MenuItem key={name} value={name}>
                            <Stack direction="row" alignItems="center">
                                <Typography sx={{ mr: 2 }}>{name}</Typography>
                                <div style={{ height: 20 }}>
                                    {colors.map((c) => (
                                        <div
                                            key={c}
                                            style={{
                                                width: 10,
                                                height: 20,
                                                backgroundColor: c,
                                                display: 'inline-block',
                                            }}
                                        />
                                    ))}
                                </div>
                            </Stack>
                        </MenuItem>
                    ))}
                </TextField>
        </Grid>
        </Paper>
        </Grid>
    );
};

export default LogDataPlot;